import { useEffect, useState } from "react";
import { Users, Mail, Sparkles } from "lucide-react";
import api from "../api";

/**
 * Lightweight social-proof strip.
 * Shows real-time counters of backyard dreamers / newsletter subs / designs made.
 * Handles empty state gracefully (never shows 0 — falls back to gentle copy).
 */
export default function SocialProof() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get("/stats/public")
      .then(({ data }) => { if (!cancel) setStats(data); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) return null;

  // Only show meaningful numbers — hide the whole component if we're basically empty
  const shouldRender = stats && (
    (stats.backyard_dreamers || 0) >= 1 ||
    (stats.subscribers || 0) >= 3 ||
    (stats.designs_created || 0) >= 3
  );

  if (!shouldRender) return null;

  return (
    <section className="max-w-6xl mx-auto px-6 lg:px-12 py-10" data-testid="social-proof-strip">
      <div className="bb-card p-6 md:p-8 bg-[#F4ECE1]">
        <p className="overline text-[#D47C59] mb-4 text-center">The community is growing 🌱</p>
        <div className="grid grid-cols-3 gap-4 md:gap-8">
          <Counter
            icon={Users}
            value={stats.backyard_dreamers}
            singular="backyard dreamer"
            plural="backyard dreamers"
            testid="stat-dreamers"
          />
          <Counter
            icon={Mail}
            value={stats.subscribers}
            singular="on Linda's list"
            plural="on Linda's list"
            testid="stat-subscribers"
          />
          <Counter
            icon={Sparkles}
            value={stats.designs_created}
            singular="AI design created"
            plural="AI designs created"
            testid="stat-designs"
          />
        </div>
      </div>
    </section>
  );
}

function Counter({ icon: Icon, value, singular, plural, testid }) {
  const n = value || 0;
  return (
    <div className="text-center" data-testid={testid}>
      <div className="w-9 h-9 rounded-full bg-[#2D4A3E] text-[#F9F6F0] flex items-center justify-center mx-auto mb-2">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <p className="font-serif text-3xl md:text-4xl font-light text-[#1A2F25] tabular-nums" style={{ fontStyle: "normal" }}>{n.toLocaleString()}</p>
      <p className="text-xs md:text-sm text-[#5C6B62] font-light mt-1">
        {n === 1 ? singular : plural}
      </p>
    </div>
  );
}
